import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "../components/ui/drawer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";
import { CaretDown, Check } from "@phosphor-icons/react";

const CURRENCIES = ["USD", "CAD", "CNY"];

export function QuoteSettingsSheet({ open, onOpenChange, cart, onUpdated }) {
  const [name, setName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [coefficient, setCoefficient] = useState("1.0");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && cart) {
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
      setName(cart.name || "");
      setCustomerId(cart.customer_id || null);
      setCurrency(cart.currency || "USD");
      setCoefficient(String(cart.price_coefficient ?? 1.0));
    }
  }, [open, cart]);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.put(`/carts/${cart.id}`, {
        name: name.trim(),
        customer_id: customerId,
        currency,
        price_coefficient: parseFloat(coefficient) || 1.0,
      });
      toast.success("Quotation updated");
      onUpdated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  if (!cart) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mobile-app rounded-t-[28px] border-[#E7E5E4] max-h-[90vh]" data-testid="mobile-quote-settings-sheet">
        <DrawerHeader>
          <DrawerTitle className="font-display text-2xl text-[#1C1917]">Quotation Settings</DrawerTitle>
        </DrawerHeader>
        <form onSubmit={handleSave} className="px-5 pb-5 space-y-5 overflow-y-auto">
          <div>
            <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Quote Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="mobile-quote-settings-name-input"
              className="mt-2 h-13 rounded-[14px] border-[#E7E5E4] text-base"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Customer</Label>
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              data-testid="mobile-quote-settings-customer-trigger"
              className="mt-2 w-full h-13 rounded-[14px] border border-[#E7E5E4] bg-white px-4 flex items-center justify-between text-base"
            >
              <span className={selectedCustomer ? "text-[#1C1917]" : "text-[#78716C]"}>
                {selectedCustomer ? selectedCustomer.name : "No customer"}
              </span>
              <CaretDown size={16} className="text-[#78716C]" />
            </button>
            {pickerOpen && (
              <div className="mt-2 max-h-44 overflow-y-auto rounded-[14px] border border-[#E7E5E4] bg-white divide-y divide-[#F1F0EE]">
                <button type="button" onClick={() => { setCustomerId(null); setPickerOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-[#78716C]">
                  No customer
                </button>
                {customers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCustomerId(c.id); setPickerOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-[#1C1917] flex items-center justify-between"
                  >
                    {c.name}
                    {customerId === c.id && <Check size={16} className="text-[#1E3A8A]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Currency</Label>
              <div className="mt-2 flex gap-1.5">
                {CURRENCIES.map((cur) => (
                  <button
                    key={cur}
                    type="button"
                    onClick={() => setCurrency(cur)}
                    data-testid={`mobile-quote-settings-currency-${cur}`}
                    className={`flex-1 h-11 rounded-[12px] text-sm font-semibold border transition-colors duration-200 ${
                      currency === cur ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-[#78716C] border-[#E7E5E4]"
                    }`}
                  >
                    {cur}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">Price Coefficient</Label>
              <Input
                type="number"
                step="0.01"
                value={coefficient}
                onChange={(e) => setCoefficient(e.target.value)}
                data-testid="mobile-quote-settings-coefficient-input"
                className="mt-2 h-11 rounded-[12px] border-[#E7E5E4] text-base"
              />
            </div>
          </div>

          <DrawerFooter className="px-0">
            <button
              type="submit"
              disabled={submitting}
              data-testid="mobile-quote-settings-save"
              className="press-scale w-full h-14 rounded-[16px] bg-[#1E3A8A] text-white font-semibold text-base transition-colors duration-200 disabled:opacity-60"
            >
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
